import { AxiosInstance } from 'axios';
import { ApolloClient, QueryOptions } from '@apollo/client';
import {
  CatalogueSearchParameters,
  CatalogueSearchResult,
  GetGraphqlDataResponse,
  GetStreetTypesAndApartmentTypesQueryResponse,
  MtpBuilding,
  Person, PersonDataResponse,
  Region,
  Residence,
  Settlement, Ticket,
} from '@/types/types';

import { axiosClient } from '@/services/AxiosClient';
import { apolloClient } from '@/services/ApolloClient';
import {
    GET_PHOTO_CONTENT_QUERY,
    GET_REGIONS_QUERY,
    GET_RESIDENCE_FULL_INFO_BY_ID_QUERY,
    GET_RESIDENCE_FULL_INFO_WITH_ADDRESS_BY_ID_QUERY,
    GET_RESIDENCES_QUERY,
    GET_RESIDENCES_WITH_AVAILABLE_PLACES,
    GET_RESIDENCES_WITH_AVAILABLE_PLACES_TOTAL_COUNT,
    GET_RESIDENCES_WITH_ID_COUNT_QUERY,
    GET_RESIDENCES_WITH_ID_QUERY,
    GET_RESIDENCES_WITH_SEARCH_STRING_COUNT_QUERY,
    GET_RESIDENCES_WITH_SEARCH_STRING_QUERY, GET_SETTLEMENT_BY_ID_QUERY,
    GET_SETTLEMENT_WITH_SEARCH_STRING_AND_REGION_COUNT_QUERY,
    GET_SETTLEMENT_WITH_SEARCH_STRING_AND_REGION_QUERY,
    GET_SETTLEMENT_WITH_SEARCH_STRING_COUNT_QUERY,
    GET_SETTLEMENT_WITH_SEARCH_STRING_QUERY,
    GET_STREET_TYPES_AND_BUILDING_TYPES_QUERY,
} from '@/services/queries';
import {mtpItemPerPage} from "@/common/common";

/**
 * Interface for service
 */
export interface ICatalogueService {
  loadCatalogue(searchParams: CatalogueSearchParameters): Promise<CatalogueSearchResult>;
}

/**
 * Catalogue service
 */
class CatalogueService implements ICatalogueService {
  /**
   * Constructor for the BackOfficeTicket
   */
  constructor(private api: AxiosInstance, private apolloClient: ApolloClient) {}

  /**
   * Makes Query GraphQl server
   */
  private query = async (options: QueryOptions) => {
    const res = await this.apolloClient.query(options);
    if (!res.errors) {
      return res.data;
    }

    throw res.errors;
  };

  async loadTotalAvailableSpaces(regionId?: number): Promise<number> {
    try {
      const res = await this.api({
        method: 'get',
        url: '/api/v1/vpo/public/get-free-beds-count',
        params: {
          regionId,
        },
      });

      return res?.data?.freeBedsCount || 0;
    } catch (e) {
      console.error('Error loading total available spaces\n', e);
      return 0;
    }
  }

  async loadCatalogue(searchParams: CatalogueSearchParameters): Promise<CatalogueSearchResult> {
    try {
      const filters: {
        and: any[];
      } = {
        and: [
          { settlementId: { neq: null } },
          {
            files: {
              any: true,
            },
          },
        ],
      };

      // selectors and search string
      if (searchParams?.regionId) {
        filters.and.push({
          settlement: { district: { region: { id: { eq: searchParams?.regionId } } } },
        });
      }
      if (searchParams?.settlementId) {
        filters.and.push({
          settlement: { id: { eq: searchParams?.settlementId } },
        });
      }
      if (searchParams?.searchString) {
        const parsedId = parseInt(searchParams.searchString, 10);

        if (!isNaN(parsedId) && Number.isInteger(parsedId)) {
          filters.and.push({
            id: { eq: parsedId },
          });
        }
      }
      
      const tenancyTypes = [
        searchParams?.freeAccommodation && 'FREE',
        searchParams?.paidAccommodation && 'PAID',
      ].filter(Boolean);

      if (tenancyTypes.length === 1) {
        filters.and.push({ tenancyType: { eq: tenancyTypes[0] } });
      } else if (tenancyTypes.length === 2) {
        filters.and.push({ tenancyType: { in: tenancyTypes } });
      }
      
      // checkboxes
        if (searchParams?.pets_available) {
            filters.and.push({
              countRoomsPetsFree: { gt: 0 },
            });
        }
        if (searchParams?.disabled_or_reduced_mobility_available) {
            filters.and.push({
              countBedsLowMobilityFree: { gt: 0 },
            });
        }
        if (searchParams?.space_available) {
            filters.and.push({
              countBedsFree: { gt: 0 },
            });
        }
        if (searchParams?.family_rooms) {
            filters.and.push({
              hasFamilyRooms: { eq: true },
            });
        }
        if (searchParams?.female_rooms) {
            filters.and.push({ hasFemaleRooms: { eq: true } });
        }
        if (searchParams?.male_rooms) {
            filters.and.push({ hasMaleRooms: { eq: true } });
        }
        if (searchParams?.hasWc) {
            filters.and.push({ hasWc: { eq: true } });
        }
        if (searchParams?.hasKitchen) {
            filters.and.push({ hasKitchen: { eq: true } });
        }
        if (searchParams?.hasBathroom) {
            filters.and.push({ hasBathroom: { eq: true } });
        }
        if (searchParams?.hasElevator) {
            filters.and.push({ hasElevator: { eq: true } });
        }
        if (searchParams?.countBedsTwoStory) {
            filters.and.push({ countBedsTwoStory: { gt: 0 } });
        }
        if (searchParams?.hasAvailablePlaces) {
          filters.and.push({ countBedsFree: {gt: 0}});
        }

      const variables = {
        take: mtpItemPerPage,
        skip: ((searchParams?.page ?? 1) - 1) * mtpItemPerPage,
        filters,
      };

      const res = await this.query({
        query: GET_RESIDENCES_QUERY,
        variables,
      });

      return res?.data;
    } catch (e: any) {
      console.error('Error loading catalogue\n', e);

      throw {
        totalCount: 0,
        items: [],
        message: e?.message,
      };

      // ---- debug
      // let res = searchParams?.page == 1 ? tempPage1 : tempPage2
      //
      // return {
      //   totalCount: 2500,
      //   data: res?.data
      // }
      // ---- debug
    }
  }

    async getSettlementById(id: number) {
        try {
            const variables = {
                id: id,
                skip: 0,
                take: 1,
            };
            
            const settlementsResponse = await this.query({
                query: GET_SETTLEMENT_BY_ID_QUERY,
                variables,
            });

            if (settlementsResponse.data?.items.length > 0) {
                return settlementsResponse.data?.items[0];
            }
            return null;
        } catch (e) {
            console.error(e);

            return [];
        }
    }
    
  async searchSettlements(searchString?: string, regionId?: number) {
    try {
      const variables = {
        searchString: searchString || '',
        regionId: regionId,
      };
      const countResponse = await this.query({
        query: regionId
          ? GET_SETTLEMENT_WITH_SEARCH_STRING_AND_REGION_COUNT_QUERY
          : GET_SETTLEMENT_WITH_SEARCH_STRING_COUNT_QUERY,
        variables,
      });
      const loadItemsArr: Promise<GetGraphqlDataResponse<Settlement>>[] = [];
      for (let i = 0; i < countResponse.data.totalCount; i += 1000) {
        const variablesExtended = {
          ...variables,
          skip: i,
          take: 1000,
        };
        loadItemsArr.push(
          this.query({
            query: regionId
              ? GET_SETTLEMENT_WITH_SEARCH_STRING_AND_REGION_QUERY
              : GET_SETTLEMENT_WITH_SEARCH_STRING_QUERY,
            variables: variablesExtended,
          }),
        );
      }

      const loadItemsRes = await Promise.all(loadItemsArr);
      let allItems: Settlement[] = [];

      for (const loadedItem of loadItemsRes) {
        allItems = allItems.concat(...loadedItem.data.items);
      }

      return allItems;
    } catch (e) {
      console.error(e);

      return [];
    }
  }

  async getRegionsAsync(): Promise<Region[]> {
    try {
      const loadItemsRes = await this.query({
        query: GET_REGIONS_QUERY,
      });
      return loadItemsRes.data.items;
    }
    catch (e) {
      console.error(e);

      return [];
    }
  }

  async getRegionsWithAvailableMtpsAsync(): Promise<Region[]> {
    try {
      const mtpsWithAvailableSpacesCount = await this.query({
        query: GET_RESIDENCES_WITH_AVAILABLE_PLACES_TOTAL_COUNT,
      });
      const mtpsWithAvailableSpaces: Residence[] = [];
      for (let i = 0; i < mtpsWithAvailableSpacesCount.data.totalCount; i += 1000) {
        const variables = {
          skip: i,
          take: 1000,
        };
        const res = await this.query({
          query: GET_RESIDENCES_WITH_AVAILABLE_PLACES,
          variables
        });
        mtpsWithAvailableSpaces.push(...res.data.items);
      }
      const regionsWithPlaces = mtpsWithAvailableSpaces.reduce<Region[]>((acc , item) => {
        if (item.settlement?.district?.region?.id && !acc.some(x => x.id === item.settlement?.district?.region?.id)) {
          acc.push(item.settlement?.district?.region);
        }
        return acc;
      }, [])
      // const loadItemsRes = await this.query({
      //   query: GET_REGIONS_QUERY,
      // });
      return regionsWithPlaces;
    } catch (e) {
      console.error(e);

      return [];
    }
  }

  async getPhotoContent(id: number): Promise<number[]> {
    try {
      const variables = {
        id,
      };
      const loadItemsRes = await this.query({
        query: GET_PHOTO_CONTENT_QUERY,
        variables,
      });
      return loadItemsRes?.data?.items?.[0]?.fileContent || [];
    } catch (e) {
      console.error(e);

      return [];
    }
  }

  async getPersonData(): Promise<PersonDataResponse | undefined> {
    try {
      const res = await this.api({
        method: 'get',
        url: '/api/v1/vpo/public/person/profile',
      });

      return res?.data;
    } catch (e: any) {
      if(e?.status == 401) {
        return {
          personProfile: {
            unauthorized: true
          }
        }
      }
      return undefined;
    }
  }

  async getStreetTypesAndApartmentTypes(): Promise<GetStreetTypesAndApartmentTypesQueryResponse> {
    return await this.query({
      query: GET_STREET_TYPES_AND_BUILDING_TYPES_QUERY,
    });
  }

  async createTicket(ticketData: Ticket): Promise<any> {
    try {
      const res = await this.api({
        method: 'post',
        url: '/api/v1/vpo/public/person/create-ticket',
        data: {
          ...ticketData,
        },
      });

      return res?.data;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }
  
  async updateTicket(ticketData: Ticket): Promise<any> {
    try {
      const res = await this.api({
        method: 'post',
        url: '/api/v1/vpo/public/person/update-ticket',
        data: {
          ...ticketData,
        },
      });

      return res?.data;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }
  
  async updatePerson(personData: Person): Promise<any> {
    try {
      const res = await this.api({
        method: 'post',
        url: '/api/v1/vpo/public/person/profile/update',
        data: {
          ...personData,
        },
      });

      return res?.data;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  async declineReservation(id: any) {
    try {
      const res = await this.api({
        method: 'post',
        url: '/api/v1/vpo/public/person/cancel-ticket',
        data: {
          id,
        },
      });

      return res?.data;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  async findResidences(searchString: string) {
    try {
      if (!searchString) {
        return [];
      }

      const variables = {
        searchString: searchString,
        searchStringId: -1,
      };

      const parsedId = parseInt(searchString, 10);
      if (!isNaN(parsedId) && Number.isInteger(parsedId)) {
        variables.searchStringId = parsedId;
      }

      const countResponse = await this.query({
        query: parsedId
          ? GET_RESIDENCES_WITH_ID_COUNT_QUERY
          : GET_RESIDENCES_WITH_SEARCH_STRING_COUNT_QUERY,
        variables,
      });
      const loadItemsArr: Promise<GetGraphqlDataResponse<MtpBuilding>>[] = [];
      for (let i = 0; i < countResponse.data.totalCount; i += 1000) {
        const variablesExtended = {
          ...variables,
          skip: i,
          take: 1000,
        };
        loadItemsArr.push(
          this.query({
            query: parsedId
              ? GET_RESIDENCES_WITH_ID_QUERY
              : GET_RESIDENCES_WITH_SEARCH_STRING_QUERY,
            variables: variablesExtended,
          }),
        );
      }

      const loadItemsRes = await Promise.all(loadItemsArr);
      let allItems: MtpBuilding[] = [];

      for (const loadedItem of loadItemsRes) {
        allItems = allItems.concat(...loadedItem.data.items);
      }

      return allItems;
    } catch (e) {
      console.error(e);

      return [];
    }
  }

  async loadFullResidenceInfoAsync(residenceId: number): Promise<Residence | null> {
    try {
      const loadedItem = await this.query({
        query: GET_RESIDENCE_FULL_INFO_BY_ID_QUERY,
        variables: {
            residenceId,
        },
      });

      return loadedItem.data.items?.[0] as Residence;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

    async loadFullResidenceInfoWithAddressAsync(residenceId: number): Promise<Residence | null> {
        try {
            const loadedItem = await this.query({
                // query: isAuthorized
                //     ? GET_RESIDENCE_FULL_INFO_WITH_ADDRESS_BY_ID_QUERY
                //     : GET_RESIDENCE_FULL_INFO_BY_ID_QUERY,
                query: GET_RESIDENCE_FULL_INFO_WITH_ADDRESS_BY_ID_QUERY,
                variables: {
                    residenceId
                },
            });
            return loadedItem.data.items?.[0] as Residence;
        } catch (e) {
            console.error(e);
            return null;
        }
    }

  async sendOtp(phone: string) {
    try {
      const res = await this.api({
        method: 'post',
        url: '/api/v1/vpo/public/person/otp/send',
        data: {
          phone: phone,
        },
      });

      return res?.data;
    } catch (e: any) {
      return {
        code: 'ERROR',
        message: e?.message,
      };
    }
  }

  async resendOtp(phone: string) {
    try {
      const res = await this.api({
        method: 'post',
        url: '/api/v1/vpo/public/person/otp/resend',
        data: {
          phone: phone,
        },
      });

      return res?.data;
    } catch (e: any) {
      return {
        code: 'ERROR',
        message: e?.message,
      };
    }
  }

  async verifyOtp(
    isLogin: boolean,
    phone: string,
    otp: string,
    lastName?: string,
    firstName?: string,
    patronymic?: string,
  ) {
    try {
      const res = await this.api({
        method: 'post',
        url: isLogin
          ? '/api/v1/vpo/public/person/otp/verify'
          : '/api/v1/vpo/public/person/register',
        data: {
          phone,
          otp,
          lastName,
          firstName,
          patronymic,
        },
      });

      return res?.data;
    } catch (e: any) {
      return {
        code: 'ERROR',
        message: e?.message,
      };
    }
  }
}

export default new CatalogueService(axiosClient, apolloClient);
